<script lang="ts">
import { Route } from "vue-router"
import { LocationActions } from "@/store/location"

export default {
    name: "LocationFloatingBarOptions",
    props: {
        notificationCount: {
            type: Number,
            required: true,
        },
    },
    computed: {
        route(): Route {
            return this.$route
        },
        companyId() {
            return this.route.params.companyId
        },
        locationGroupId() {
            return this.route.params.locationGroupId
        },
        locationId() {
            return this.route.params.locationId
        },
        hasEvseFeature(): boolean {
            return this.$store.getters["location/hasEvseFeature"]
        },
        isEvChargingRouteActive(): boolean {
            return this.route.path.startsWith(`${this.locationUrlPrefix}/ev-charging`)
        },
        locationUrlPrefix(): string {
            const segments = []

            if (this.companyId) {
                segments.push(`companies/${this.companyId}`)
            }

            if (this.locationGroupId) {
                segments.push(`location-groups/${this.locationGroupId}`)
            }

            if (this.locationId) {
                segments.push(`locations/${this.locationId}`)
            }

            return `/${segments.join("/")}`
        },
    },
    watch: {
        locationId: {
            immediate: true,
            handler() {
                this.loadLocation()
            },
        },
    },
    methods: {
        isRouteActive(route: any) {
            return route.fullPath === this.route.path
        },
        loadLocation() {
            if (!this.locationId) return
            this.$store.dispatch(LocationActions.FETCH_LOCATION, { locationId: this.locationId })
        },
        openNotificationsModal() {
            this.$emit("open-notifications-modal")
        },
    },
}
</script>

<template>
    <ul class="nav location-nav display_flex_evenly">
        <li class="nav-item">
            <router-link
                :to="{ path: locationUrlPrefix }"
                v-slot="{ href, route, navigate }"
                custom
            >
                <a
                    :title="$t('floating_bar_options.overview')"
                    class="nav-link"
                    :class="[isRouteActive(route) && 'router-link-exact-active']"
                    :href="href"
                    @click="navigate"
                >
                    <span class="icon icon-home"></span>
                </a>
            </router-link>
        </li>
        <li class="nav-item">
            <router-link
                :to="{ path: locationUrlPrefix + '/charts' }"
                v-slot="{ href, route, navigate }"
                custom
            >
                <a
                    :title="$t('floating_bar_options.charts')"
                    class="nav-link"
                    :class="[isRouteActive(route) && 'router-link-exact-active']"
                    :href="href"
                    @click="navigate"
                >
                    <span class="icon icon-chart"></span>
                </a>
            </router-link>
        </li>
        <li v-if="hasEvseFeature" class="nav-item">
            <router-link
                :to="{ path: locationUrlPrefix + '/ev-charging' }"
                v-slot="{ href, navigate }"
                custom
            >
                <a
                    :title="$t('floating_bar_options.ev_charging')"
                    class="nav-link"
                    :class="[isEvChargingRouteActive && 'router-link-exact-active']"
                    :href="href"
                    @click="navigate"
                >
                    <span class="icon icon-car"></span>
                </a>
            </router-link>
        </li>
        <li class="nav-item">
            <router-link
                :to="{ path: locationUrlPrefix + '/strategies' }"
                v-slot="{ href, route, navigate }"
                custom
            >
                <a
                    :title="$t('floating_bar_options.strategies')"
                    class="nav-link"
                    :class="[isRouteActive(route) && 'router-link-exact-active']"
                    :href="href"
                    @click="navigate"
                >
                    <span class="icon icon-energy"></span>
                </a>
            </router-link>
        </li>
        <li class="nav-item">
            <div
                :title="$t('floating_bar_options.notifications')"
                class="nav-link cursor-pointer alert-indicator"
                @click="openNotificationsModal"
            >
                <span class="icon icon-bell"></span>
                <span class="alarms-count" v-if="notificationCount > 0">{{
                    notificationCount
                }}</span>
            </div>
        </li>
    </ul>
</template>

<style lang="scss">
@media (max-width: 768px) {
    .display_flex_evenly {
        gap: 1rem;
    }
}
</style>
